import { Outlet, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const navItems = [
  { name: "Home", path: "/" },
  { name: "FAQ", path: "/faq" },
  { name: "Pledge", path: "/pledge" },
  { name: "Resources", path: "/resources" },
  { name: "Donate", path: "/donate" },
  { name: "Contact", path: "/contact" },
];

export const Layout = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header
        className={`sticky top-0 z-50 transition-all duration-200 ${
          isScrolled
            ? "bg-[color:var(--pws-off)]/95 backdrop-blur border-b border-[color:var(--pws-line)] shadow-sm"
            : "bg-transparent"
        }`}
        data-testid="main-navigation"
      >
        <nav className="container-pws py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="logo-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_nostrings/artifacts/i0zj9mrd_pws%20logo%20on%20transparent.png"
                alt="Panties Without Strings"
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors link-hover ${
                    location.pathname === item.path
                      ? "text-[color:var(--pws-plum)]"
                      : "text-[color:var(--pws-ink)] hover:text-[color:var(--pws-plum)]"
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(" ", "-")}`}
                >
                  {item.name}
                </Link>
              ))}
              <Button
                asChild
                className="bg-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum-hover)] text-white btn-hover"
                data-testid="nav-get-free-pair-btn"
              >
                <Link to="/request">Get a free pair</Link>
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-[color:var(--pws-charcoal)]"
                    data-testid="mobile-menu-btn"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-[color:var(--pws-off)] border-[color:var(--pws-line)]">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <SheetDescription className="sr-only">Main navigation links for Panties Without Strings</SheetDescription>
                  <div className="mb-6">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_nostrings/artifacts/i0zj9mrd_pws%20logo%20on%20transparent.png"
                      alt="Panties Without Strings"
                      className="h-8 w-auto"
                    />
                  </div>
                  <div className="flex flex-col gap-6 mt-4">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`text-lg font-medium ${
                          location.pathname === item.path
                            ? "text-[color:var(--pws-plum)]"
                            : "text-[color:var(--pws-ink)]"
                        }`}
                        data-testid={`mobile-nav-${item.name.toLowerCase().replace(" ", "-")}`}
                      >
                        {item.name}
                      </Link>
                    ))}
                    <Button
                      asChild
                      className="bg-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum-hover)] text-white w-full mt-4"
                      data-testid="mobile-get-free-pair-btn"
                    >
                      <Link to="/request" onClick={() => setMobileOpen(false)}>
                        Get a free pair
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[color:var(--pws-cream)] border-t border-[color:var(--pws-line)] py-8" data-testid="footer">
        <div className="container-pws">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-[color:var(--pws-stone)]">
              <span className="font-medium">A Safe Work Coalition campaign</span>
              <span className="mx-2">•</span>
              <span>18+ only</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-[color:var(--pws-stone)]">
              <Link to="/pledge" className="hover:text-[color:var(--pws-plum)] transition-colors">
                Data Pledge
              </Link>
              <Link to="/contact" className="hover:text-[color:var(--pws-plum)] transition-colors">
                Contact
              </Link>
              <Link to="/donate" className="hover:text-[color:var(--pws-plum)] transition-colors">
                Donate
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
